import { useEffect, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "../../../examples/Tables/DataTable";
import data from "../../dashboard/components/Projects/data";

import useApi from "../../../components/Utils/api";
import toast from "react-hot-toast";
import Papa from "papaparse";
import PermissionControl from "../../../components/Utils/PermissionControl";

import MdrPosModal from "./MdrPosModal";
import MdrQrModal from "./MdrQrModal";

function MdrMasterChannels() {
  const { mdrPosMasterColumns, mdrQrMasterColumns } = data();
  const api = useApi();

  const [activeTab, setActiveTab] = useState(0);

  // ── POS state ──
  const [posMasters, setPosMasters] = useState([]);
  const [availableSegments, setAvailableSegments] = useState([]);
  const [posModalOpen, setPosModalOpen] = useState(false);
  const [posEditRow, setPosEditRow] = useState(null);
  const [posToDelete, setPosToDelete] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // ── QR state ──
  const [qrData, setQrData] = useState(null);
  const [qrRows, setQrRows] = useState([]);
  const [qrModalOpen, setQrModalOpen] = useState(false);

  // ══════════════ POS ══════════════
  const openAddPos = () => {
    setPosEditRow(null);
    setPosModalOpen(true);
  };

  const openEditPos = (item) => {
    setPosEditRow(item);
    setPosModalOpen(true);
  };

  const openDeletePos = (item) => {
    setPosToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeletePos = () => {
    if (!posToDelete) return;
    api
      .delete("master/pos/" + posToDelete.mcId)
      .then((response) => {
        if (
          response.status === 204 ||
          response.status === 200 ||
          (response.data && response.data.responseCode === 200)
        ) {
          toast.success("POS MDR deleted for " + (posToDelete.segmentName || posToDelete.mccCode));
          setDeleteDialogOpen(false);
          setPosToDelete(null);
          getPosMasters();
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }
        toast.error("Failed to delete POS MDR");
      });
  };

  const getPosMasters = () => {
    api
      .post("master/pos/list")
      .then((response) => {
        if (response.data && response.data.responseCode === 200) {
          const rows = (response.data.data || []).map((item) => {
            const action = (
              <MDBox display="flex" alignItems="center" justifyContent="center" lineHeight={1}>
                <PermissionControl permission={{ restrict: true, module: "Masters", subModule: "UpdateMdrMaster" }}>
                  <IconButton size="small" color="info" onClick={() => openEditPos(item)}>
                    <Icon fontSize="small">edit</Icon>
                  </IconButton>
                </PermissionControl>
                <PermissionControl permission={{ restrict: true, module: "Masters", subModule: "DeleteMdrMaster" }}>
                  <IconButton size="small" color="error" onClick={() => openDeletePos(item)}>
                    <Icon fontSize="small">delete</Icon>
                  </IconButton>
                </PermissionControl>
              </MDBox>
            );
            return { ...item, action };
          });
          setPosMasters(rows);
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          toast.error("You do not have permission to perform this action.", { id: "PermissionError" });
          return;
        }
        toast.error("Failed to load POS MDR list");
      });
  };

  const getAvailableSegments = () => {
    api
      .post("master/pos/available-segments")
      .then((response) => {
        if (response.data && response.data.responseCode === 200) {
          setAvailableSegments(response.data.data || []);
        }
      })
      .catch(() => {
        // Non-blocking: dropdown simply stays empty
      });
  };

  const refreshPos = () => {
    getPosMasters();
    getAvailableSegments();
  };

  const generateCSV = () => {
    if (posMasters.length === 0) return "";

    const filteredColumns = mdrPosMasterColumns.filter((column) => column.Header !== "Action");
    const headers = filteredColumns.map((column) => column.Header);

    const rows = posMasters.map((item) =>
      filteredColumns.map((column) => {
        const accessor = column.accessor;
        return typeof accessor === "function" ? accessor(item) : item[accessor];
      })
    );

    return Papa.unparse([headers, ...rows], { quotes: true });
  };

  const handleDownloadCSV = () => {
    if (posMasters.length === 0) {
      toast.error("No records found to download.");
      return;
    }
    const csvData = generateCSV();
    const currentDate = new Date().toISOString().slice(0, 10);
    const currentTime = new Date().toLocaleTimeString().replace(/:/g, "-");
    const fileName = `MdrMasterPOS_${currentDate}_${currentTime}.csv`;

    const blob = new Blob([csvData], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // ══════════════ QR ══════════════
  const getQr = () => {
    api
      .post("master/qr/get")
      .then((response) => {
        if (response.data && response.data.responseCode === 200) {
          const payload = response.data.data;
          setQrData(payload || null);
          if (payload) {
            const action = (
              <MDBox display="flex" alignItems="center" justifyContent="center" lineHeight={1}>
                <PermissionControl permission={{ restrict: true, module: "Masters", subModule: "UpdateMdrMaster" }}>
                  <IconButton size="small" color="info" onClick={() => setQrModalOpen(true)}>
                    <Icon fontSize="small">edit</Icon>
                  </IconButton>
                </PermissionControl>
              </MDBox>
            );
            setQrRows([{ ...payload, action }]);
          } else {
            setQrRows([]);
          }
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          toast.error("You do not have permission to perform this action.", { id: "PermissionError" });
          return;
        }
        setQrData(null);
        setQrRows([]);
      });
  };

  useEffect(() => {
    refreshPos();
    getQr();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={12}>
            <Card>
              <MDBox px={2} pt={2}>
                <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)}>
                  <Tab label="POS" />
                  <Tab label="QR" />
                  <Tab label="IPG" />
                </Tabs>
              </MDBox>

              {/* ═══ POS ═══ */}
              {activeTab === 0 && (
                <MDBox p={2}>
                  <MDBox display="flex" justifyContent="flex-end" gap={1} mb={2}>
                    <MDButton variant="gradient" color="info" onClick={handleDownloadCSV}>
                      Export
                    </MDButton>
                    <PermissionControl permission={{ restrict: true, module: "Masters", subModule: "CreateMdrMaster" }}>
                      <MDButton variant="gradient" color="error" onClick={openAddPos}>
                        Add Segment MDR
                      </MDButton>
                    </PermissionControl>
                  </MDBox>
                  <DataTable
                    table={{ columns: mdrPosMasterColumns, rows: posMasters }}
                    showTotalEntries={true}
                    isSorted={true}
                    noEndBorder
                    entriesPerPage={true}
                    canSearch={true}
                  />
                  <MDBox mt={2}>
                    <MDTypography variant="caption" color="text">
                      Monthly rental per MID: LKR 6,000.00 &middot; TPA fee (monthly): LKR 187,500.00
                    </MDTypography>
                  </MDBox>
                </MDBox>
              )}

              {/* ═══ QR ═══ */}
              {activeTab === 1 && (
                <MDBox p={2}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <MDTypography variant="caption" color="text">
                      QR MDR rates apply uniformly to all merchants.
                    </MDTypography>
                    <PermissionControl
                      permission={{
                        restrict: true,
                        module: "Masters",
                        subModule: qrData ? "UpdateMdrMaster" : "CreateMdrMaster",
                      }}
                    >
                      <MDButton variant="gradient" color="error" onClick={() => setQrModalOpen(true)}>
                        {qrData ? "Edit QR MDR" : "Add QR MDR"}
                      </MDButton>
                    </PermissionControl>
                  </MDBox>
                  <DataTable
                    table={{ columns: mdrQrMasterColumns, rows: qrRows }}
                    showTotalEntries={true}
                    isSorted={false}
                    noEndBorder
                    entriesPerPage={false}
                    canSearch={false}
                  />
                </MDBox>
              )}

              {/* ═══ IPG (placeholder) ═══ */}
              {activeTab === 2 && (
                <MDBox p={6} textAlign="center">
                  <Icon fontSize="large" color="disabled">
                    public
                  </Icon>
                  <MDTypography variant="h6" color="text" mt={1}>
                    IPG MDR configuration
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    Coming soon.
                  </MDTypography>
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* POS Add/Edit modal */}
      <MdrPosModal
        open={posModalOpen}
        onClose={() => setPosModalOpen(false)}
        editRow={posEditRow}
        availableSegments={availableSegments}
        getMasters={refreshPos}
      />

      {/* QR Edit modal */}
      <MdrQrModal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        qrData={qrData}
        getQr={getQr}
      />

      {/* Delete confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} aria-labelledby="delete-pos-dialog-title">
        <DialogTitle id="delete-pos-dialog-title">Delete POS MDR</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the POS MDR for{" "}
            <strong>{posToDelete?.segmentName || posToDelete?.mccCode}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setPosToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleDeletePos}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default MdrMasterChannels;
